import React, { useEffect } from 'react';
import routes from '../../../../../Routes/routes';
import { getUrlParameters, TAB_INDEX } from "../../External/Model/Model";
import { REQUEST_ACCESS_STEP_MODEL } from "../Model/Model";

/**
 * FindMySelfCreateVisitorMainApp
 *
 * Component to handle the “Find Myself / Create Visitor” flow.
 * Manages step navigation, button enable/disable logic, and triggers request creation.
 *
 * @author Karen Yunuen
 * @jobTitle Full Stack Developer - IBL
 * @email kpozos@ibl.mx
 *
 * @param {Function} navigate - Function to navigate programmatically (react-router).
 * @param {Object} steps - Object representing the steps of the QR request flow.
 * @param {Function} setSteps - Function to update the steps state.
 * @param {number} activeStep - Current active step index.
 * @param {Function} setActiveStep - Function to update the active step index.
 * @param {Array} visitorList - List of selected visitors.
 * @param {Object} requestAccessItem - Request access object with details and validations.
 * @param {Function} setBtnDisabled - Function to enable/disable the Next button.
 * @param {Function} handleCreate - Function to create the request access.
 * @param {Function} navigateStep - Function to navigate to a specific step.
 * @param {Object} userData - Current user/visitor data object.
 * @param {boolean} isValidEmail - Flag indicating whether the email is valid.
 * @param {boolean} isValidPhoneNumber - Flag indicating whether the phone number is valid.
 * @param isValidDocumentId
 * @param {Function} resetData - Function to reset all form and state data.
 *
 * @param handleCreateInReservation
 * @returns {Object} An object containing handler functions:
 * - handleClickNext: Function to navigate to the next step or trigger create request.
 * - handleNavigateBack: Function to navigate to the previous step or reset data.
 */
const FindMySelfCreateVisitorMainApp = ({
                                            navigate,
                                            steps,
                                            setSteps,
                                            activeStep,
                                            setActiveStep,
                                            visitorList,
                                            requestAccessItem,
                                            setBtnDisabled,
                                            handleCreate,
                                            navigateStep,
                                            userData,
                                            isValidEmail,
                                            isValidPhoneNumber,
                                            isValidDocumentId,
                                            resetData
                                        }) => {
    const stepsAux = REQUEST_ACCESS_STEP_MODEL.FIND_MYSELF_CREATE_VISITOR;
    const title =  'request_access';
    const textBtnNext =  'next';
    const backUrl = routes.qrRequestAccess.findMySelf;

    /**
     * Set initial active step and load step data when component mounts.
     *
     * @dependencies [] - Runs only once on mount.
     */
    useEffect(() => {
        setActiveStep(2);
        setSteps({ ...stepsAux });
    }, []);

    /**
     * Enable or disable the Next button based on current step and form validation.
     *
     * @dependencies [steps, activeStep, visitorList, requestAccessItem.requestReason, requestAccessItem.privacyConsent, userData]
     */
    useEffect(() => {
        switch (activeStep) {
            case TAB_INDEX.TWO:
                if (!requestAccessItem?.privacyConsent || !userData.isValid()
                    || !isValidEmail || !isValidPhoneNumber || !isValidDocumentId)
                    setBtnDisabled(true);
                else
                    setBtnDisabled(false);
                break;
            case TAB_INDEX.THREE:
                if (!requestAccessItem?.isValid())
                    setBtnDisabled(true);
                else
                    setBtnDisabled(false);
                break;
            default:
                break;
        }
    }, [
        steps,
        activeStep,
        visitorList,
        requestAccessItem?.requestReason,
        requestAccessItem?.privacyConsent,
        userData,
        isValidEmail,
        isValidPhoneNumber,
        isValidDocumentId
    ]);

    /**
     * Handle the Next button click and navigate to the appropriate step.
     */
    const handleClickNext = () => {
        const completeSteps = {
            [TAB_INDEX.TWO]: () =>  navigateStep(TAB_INDEX.THREE),
            [TAB_INDEX.THREE]: () => navigateStep(TAB_INDEX.FOUR),
            [TAB_INDEX.FOUR]: () => handleCreate(TAB_INDEX.FIVE),
            [TAB_INDEX.FIVE]: () => navigate(getUrlParameters({ url: routes.qrRequestAccess.main }))
        };

        const action = completeSteps[activeStep];
        if (action) action();
    };

    /**
     * Handle the Back button click and navigate to the previous step or reset data.
     */
    const handleNavigateBack = () => {
        const completeSteps = {
            [TAB_INDEX.TWO]: () => {
                navigate(getUrlParameters({ url: backUrl }));
                resetData();
            },
            [TAB_INDEX.THREE]: () => navigateStep(TAB_INDEX.TWO),
            [TAB_INDEX.FOUR]: () => navigateStep(TAB_INDEX.THREE),
            [TAB_INDEX.FIVE]: () => navigateStep(TAB_INDEX.FOUR)
        };

        const action = completeSteps[activeStep];
        if (action) action();
    };

    return {
        handleClickNext,
        handleNavigateBack,
        title,
        textBtnNext
    };
};

export default FindMySelfCreateVisitorMainApp;
